import {
  deriveLoomAuthoritativeRebuildRequest,
  LoomAuthoritativeRebuildError,
  requestLoomAuthoritativeRebuild,
  type LoomAuthoritativeRebuildResult,
  type LoomRebuildBlocker,
  type LoomRebuildPhase,
  type LoomRebuildProgress,
  type LoomRebuildStatus,
} from "../../api/loomRebuild";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "./loomAuthoringHistory";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";

export interface LoomAuthoritativeRebuildRuntime {
  phase: "idle" | "unsupported" | "rebuilding" | "ready" | "error";
  requestId: number;
  requestedRevision: number | null;
  acceptedRevision: number | null;
  acceptedStateId: string | null;
  activeRequestId: number | null;
  activeRevision: number | null;
  queuedRequestId: number | null;
  queuedRevision: number | null;
  transportActive: boolean;
  progress: LoomRebuildProgress | null;
  elapsedMs: number;
  lastMeasuredBuildMs: number | null;
  failureStage: LoomRebuildPhase | null;
  failureCode: string | null;
  blockers: readonly LoomRebuildBlocker[];
  result: LoomAuthoritativeRebuildResult | null;
  error: string | null;
}

export type LoomRebuildTransport = typeof requestLoomAuthoritativeRebuild;
export type LoomRebuildStatusTransport = (
  projectId: string, signal?: AbortSignal,
) => Promise<LoomRebuildStatus>;

interface LoomAuthoritativeRebuildIntent {
  definition: ReturnType<typeof deriveLoomAuthoritativeRebuildRequest>;
  requestId: number;
  revision: number;
  stateId: string;
}

interface LoomAuthoritativeRebuildActive {
  controller: AbortController;
  intent: LoomAuthoritativeRebuildIntent;
  pollController: AbortController;
  settled: boolean;
}

const POLL_INTERVAL_MS = 500;

export const initialLoomAuthoritativeRebuildRuntime = (): LoomAuthoritativeRebuildRuntime => ({
  phase: "idle", requestId: 0, requestedRevision: null,
  acceptedRevision: null, acceptedStateId: null,
  activeRequestId: null, activeRevision: null, queuedRequestId: null, queuedRevision: null,
  transportActive: false, progress: null, elapsedMs: 0, lastMeasuredBuildMs: null,
  failureStage: null, failureCode: null, blockers: [], result: null, error: null,
});

function waitForPoll(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const finish = () => { signal.removeEventListener("abort", onAbort); resolve(); };
    const timer = window.setTimeout(finish, POLL_INTERVAL_MS);
    const onAbort = () => { window.clearTimeout(timer); finish(); };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export class LoomAuthoritativeRebuildScheduler {
  private active: LoomAuthoritativeRebuildActive | null = null;
  private current = initialLoomAuthoritativeRebuildRuntime();
  private disposed = false;
  private nextRequestId = 0;
  private observedHead: { revision: number; stateId: string } | null = null;
  private pendingLatest: LoomAuthoritativeRebuildIntent | null = null;

  constructor(
    private readonly projectId: string,
    private readonly onState: (state: LoomAuthoritativeRebuildRuntime) => void,
    private readonly transport: LoomRebuildTransport = requestLoomAuthoritativeRebuild,
    private readonly statusTransport: LoomRebuildStatusTransport | null = null,
  ) {}

  activate(): void { this.disposed = false; }

  observe(document: LoomAuthoringDocument): void {
    this.observedHead = {
      revision: document.revision,
      stateId: loomAuthoringDocumentStateId(document),
    };
  }

  synchronize(document: LoomAuthoringDocument): void {
    this.observe(document);
    const observedHead = this.observedHead;
    if (!observedHead) return;
    if (this.active && !this.intentMatchesObservedHead(this.active.intent)) {
      this.nextRequestId += 1;
      this.pendingLatest = null;
      this.abortActive(this.active);
      this.current = this.idleRuntime(this.nextRequestId);
      this.emit();
      return;
    }
    if (!this.active && ((this.current.acceptedStateId !== null
      && this.current.acceptedStateId !== observedHead.stateId)
      || (this.current.requestedRevision !== null
        && this.current.requestedRevision !== observedHead.revision))) {
      this.current = this.idleRuntime(this.current.requestId);
      this.emit();
    }
  }

  request(document: LoomAuthoringDocument, asset: LoomNativeHybridAsset): number {
    if (this.disposed) return this.nextRequestId;
    this.observe(document);
    const stateId = loomAuthoringDocumentStateId(document);
    if (this.current.phase === "ready" && this.current.acceptedRevision === document.revision
        && this.current.acceptedStateId === stateId) return this.current.requestId;
    const requestId = ++this.nextRequestId;
    const intent = {
      definition: deriveLoomAuthoritativeRebuildRequest(asset, document, `loom-rebuild-${requestId}`),
      requestId, revision: document.revision, stateId,
    };
    if (this.active) {
      this.pendingLatest = intent;
      this.publishQueuedIntent(intent);
    } else this.start(intent);
    return requestId;
  }

  cancel(): void {
    this.nextRequestId += 1;
    this.pendingLatest = null;
    if (this.active) this.abortActive(this.active);
    this.current = this.idleRuntime(this.nextRequestId);
    this.emit();
  }

  dispose(): void {
    this.disposed = true;
    this.pendingLatest = null;
    if (this.active) this.abortActive(this.active);
  }

  private idleRuntime(requestId: number): LoomAuthoritativeRebuildRuntime {
    return { ...initialLoomAuthoritativeRebuildRuntime(), requestId,
      lastMeasuredBuildMs: this.current.lastMeasuredBuildMs };
  }

  private abortActive(active: LoomAuthoritativeRebuildActive): void {
    active.settled = true;
    active.controller.abort();
    active.pollController.abort();
  }

  private start(intent: LoomAuthoritativeRebuildIntent): void {
    if (intent.definition.kind === "unavailable") {
      this.publishIntent(intent);
      return;
    }
    const active = {
      controller: new AbortController(), intent,
      pollController: new AbortController(), settled: false,
    };
    this.active = active;
    this.publishIntent(intent);
    if (this.statusTransport) void this.poll(active);
    void this.run(active);
  }

  private async run(active: LoomAuthoritativeRebuildActive): Promise<void> {
    const { controller, intent } = active;
    try {
      if (intent.definition.kind !== "available") return;
      const result = await this.transport(this.projectId, intent.definition.request, controller.signal);
      active.settled = true;
      active.pollController.abort();
      if (!this.isResultCurrent(active)) return;
      if (result.sourceRevision !== intent.revision || result.sourceStateId !== intent.stateId) {
        throw new Error("Authoritative rebuild response is stale or bound to another Pattern head");
      }
      this.current = {
        ...initialLoomAuthoritativeRebuildRuntime(), phase: "ready", requestId: intent.requestId,
        requestedRevision: intent.revision, acceptedRevision: result.sourceRevision,
        acceptedStateId: result.sourceStateId, result, elapsedMs: result.buildMs,
        lastMeasuredBuildMs: result.buildMs,
      };
      this.emit();
    } catch (error: unknown) {
      active.settled = true;
      active.pollController.abort();
      if (!this.isResultCurrent(active)) return;
      const typed = error instanceof LoomAuthoritativeRebuildError ? error : null;
      const lastProgressPhase = this.current.progress?.phase ?? null;
      this.current = {
        ...this.current, phase: "error", activeRequestId: null, activeRevision: null,
        queuedRequestId: null, queuedRevision: null, transportActive: false, progress: null,
        blockers: [], failureStage: typed?.phase ?? lastProgressPhase,
        failureCode: typed?.code ?? null,
        error: error instanceof Error ? error.message : "Authoritative rebuild failed",
      };
      this.emit();
    } finally {
      if (this.active !== active) return;
      this.active = null;
      const pending = this.pendingLatest;
      this.pendingLatest = null;
      if (!this.disposed && pending) this.start(pending);
      else if (!this.disposed && this.current.transportActive
          && this.current.activeRequestId === intent.requestId) {
        this.current = this.idleRuntime(this.current.requestId);
        this.emit();
      }
    }
  }

  private async poll(active: LoomAuthoritativeRebuildActive): Promise<void> {
    const signal = active.pollController.signal;
    while (!signal.aborted && !active.settled && this.active === active) {
      try {
        const status = await this.statusTransport!(this.projectId, signal);
        if (signal.aborted || active.settled || this.active !== active) return;
        this.applyStatus(active, status);
      } catch {
        if (signal.aborted || active.settled || this.active !== active) return;
        this.clearUnconfirmedProgress();
      }
      await waitForPoll(signal);
    }
  }

  private applyStatus(active: LoomAuthoritativeRebuildActive, status: LoomRebuildStatus): void {
    const lastMeasuredBuildMs = status.last_build_ms ?? this.current.lastMeasuredBuildMs;
    const progress = status.current_progress;
    if (progress === null) {
      if (this.current.progress || lastMeasuredBuildMs !== this.current.lastMeasuredBuildMs) {
        this.current = { ...this.current, progress: null, lastMeasuredBuildMs };
        this.emit();
      }
      return;
    }
    const expectedRequestId = active.intent.definition.kind === "available"
      ? active.intent.definition.request.requestId : "";
    if (progress.request_id !== expectedRequestId
        || progress.source_revision !== active.intent.revision
        || progress.source_state_id !== active.intent.stateId) {
      if (this.current.progress || lastMeasuredBuildMs !== this.current.lastMeasuredBuildMs) {
        this.current = { ...this.current, progress: null, lastMeasuredBuildMs };
        this.emit();
      }
      return;
    }
    const previous = this.current.progress;
    if (previous && (progress.phase_index < previous.phase_index
        || progress.elapsed_ms < previous.elapsed_ms
        || (progress.phase_index === previous.phase_index
          && progress.candidate_index !== null && previous.candidate_index !== null
          && progress.candidate_index < previous.candidate_index))) return;
    const elapsedMs = Math.floor(progress.elapsed_ms / 1_000) * 1_000;
    const changed = !previous || progress.phase !== previous.phase
      || progress.message !== previous.message
      || progress.candidate_index !== previous.candidate_index
      || progress.candidate_count !== previous.candidate_count
      || Boolean(progress.construction_trace) !== Boolean(previous.construction_trace)
      || elapsedMs !== this.current.elapsedMs
      || lastMeasuredBuildMs !== this.current.lastMeasuredBuildMs;
    if (!changed) return;
    const stableProgress = previous?.construction_trace && progress.construction_trace
      ? { ...progress, construction_trace: previous.construction_trace }
      : progress;
    this.current = { ...this.current, progress: stableProgress, elapsedMs, lastMeasuredBuildMs };
    this.emit();
  }

  private clearUnconfirmedProgress(): void {
    if (!this.current.progress) return;
    this.current = { ...this.current, progress: null };
    this.emit();
  }

  private isResultCurrent(active: LoomAuthoritativeRebuildActive): boolean {
    return !this.disposed && !active.controller.signal.aborted && this.active === active
      && active.intent.requestId === this.nextRequestId && this.pendingLatest === null
      && this.intentMatchesObservedHead(active.intent);
  }

  private intentMatchesObservedHead(intent: LoomAuthoritativeRebuildIntent): boolean {
    return this.observedHead !== null && intent.revision === this.observedHead.revision
      && intent.stateId === this.observedHead.stateId;
  }

  private publishIntent(intent: LoomAuthoritativeRebuildIntent): void {
    if (intent.definition.kind === "unavailable") {
      this.current = {
        ...this.idleRuntime(intent.requestId), phase: "unsupported", requestedRevision: intent.revision,
        blockers: intent.definition.blockers, error: intent.definition.reason,
      };
    } else {
      this.current = {
        ...this.idleRuntime(intent.requestId), phase: "rebuilding", requestedRevision: intent.revision,
        activeRequestId: intent.requestId, activeRevision: intent.revision, transportActive: true,
      };
    }
    this.emit();
  }

  private publishQueuedIntent(intent: LoomAuthoritativeRebuildIntent): void {
    if (intent.definition.kind === "unavailable") {
      this.current = {
        ...this.current, phase: "unsupported",
        requestId: intent.requestId, requestedRevision: intent.revision,
        queuedRequestId: intent.requestId, queuedRevision: intent.revision,
        blockers: intent.definition.blockers, error: intent.definition.reason,
      };
      this.emit();
      return;
    }
    this.current = {
      ...this.current, phase: this.current.transportActive ? "rebuilding" : "idle",
      requestId: intent.requestId, requestedRevision: intent.revision,
      queuedRequestId: intent.requestId, queuedRevision: intent.revision,
      blockers: [], error: null,
    };
    this.emit();
  }

  private emit(): void { if (!this.disposed) this.onState({ ...this.current }); }
}
